/**
 * @module qbLinkage/handlers/description
 * @description QB 联动管道内同步 `description`（表格字段描述信息）
 *
 * description 以 `{table_code}_{column_name}` 及带角色后缀 `_0`（指标）/ `_1`（维度）的 sub-key 存储。
 * 勾选列占比的指标另有虚拟列 `% {metric}`，其 description key 规则与指标一致（后缀 `_0`）。
 * QB 字段移除或 column_name 身份变更时，需同步裁剪或迁移对应条目，避免残留无效配置。
 */

import type { IDescriptionSetting } from '../../../../types/settings';
import type { IQBLinkageHandler } from '../types';
import { buildRecordSubKeyPatch } from '../../../../utils/fieldSettingRename';
import {
  hasNonEmptyRecordInLayers,
  patchBoth,
  pruneDescriptionForRemoved,
  migrateDescriptionIdentity,
} from './shared';

/**
 * 将 description 层的前后快照 diff 为 sub-key 级 patch。
 * 使用 buildRecordSubKeyPatch 以删除旧 key、写入新 key，而非整层覆盖。
 */
function descriptionLayerPatch(
  prev: IDescriptionSetting | undefined,
  next: IDescriptionSetting | undefined,
): { description: Record<string, unknown> } | undefined {
  const subPatch = buildRecordSubKeyPatch(prev, next);
  return subPatch ? { description: subPatch } : undefined;
}

/**
 * description 联动的统一入口，按 mode 执行裁剪或迁移。
 *
 * @param mode
 * - `prune`：QB 移除维/指时，删除对应 description sub-key
 * - `migrate`：字段身份改名（非 displayOnly）时，迁移 sub-key 前缀
 */
function applyDescription(
  changeSet: Parameters<IQBLinkageHandler['apply']>[0],
  ctx: Parameters<IQBLinkageHandler['apply']>[1],
  mode: 'prune' | 'migrate',
): ReturnType<IQBLinkageHandler['apply']> {
  // 任一层无 description 子项则跳过，避免对未配置过的图表写入空 patch
  if (!hasNonEmptyRecordInLayers(ctx.base, ctx.draft, 'description')) {
    return;
  }

  let baseDesc = ctx.base.description;
  let draftDesc = ctx.draft.description;
  let touched = false;

  // ===== prune：按移除字段裁剪 base / draft 两层 =====
  if (mode === 'prune') {
    // 指标移除：roleSuffix '0' 对应 `{prefix}`、`{prefix}_0` 及列占比虚拟列 `% {metric}` 的 key
    if (changeSet.removedMetrics.length) {
      const next = pruneDescriptionForRemoved(baseDesc, changeSet.removedMetrics, '0');
      if (next) {
        baseDesc = next;
        touched = true;
      }
      const nextD = pruneDescriptionForRemoved(draftDesc, changeSet.removedMetrics, '0');
      if (nextD) {
        draftDesc = nextD;
        touched = true;
      }
    }
    // 维度移除：roleSuffix '1' 对应 `{prefix}` 与 `{prefix}_1`
    if (changeSet.removedDimensions.length) {
      const next = pruneDescriptionForRemoved(
        baseDesc,
        changeSet.removedDimensions,
        '1',
      );
      if (next) {
        baseDesc = next;
        touched = true;
      }
      const nextD = pruneDescriptionForRemoved(
        draftDesc,
        changeSet.removedDimensions,
        '1',
      );
      if (nextD) {
        draftDesc = nextD;
        touched = true;
      }
    }
    // 明细模式 raw 列移除：UI 侧均写入 `_0` 后缀 key
    if (changeSet.removedRawColumns?.length) {
      const next = pruneDescriptionForRemoved(
        baseDesc,
        changeSet.removedRawColumns,
        '0',
      );
      if (next) {
        baseDesc = next;
        touched = true;
      }
      const nextD = pruneDescriptionForRemoved(
        draftDesc,
        changeSet.removedRawColumns,
        '0',
      );
      if (nextD) {
        draftDesc = nextD;
        touched = true;
      }
    }
  }

  // ===== migrate：字段 column_name 身份变更时迁移 sub-key =====
  if (mode === 'migrate') {
    // displayOnly 仅改展示名，description key 不变，不参与迁移
    const identityRenames = changeSet.renamed.filter((r) => !r.displayOnly);
    if (identityRenames.length) {
      const dimRenames = identityRenames
        .filter((r) => r.field.builder_type === 'dimension')
        .map((r) => ({
          snap: r.field,
          prevColumnName: r.prevColumnName,
          roleSuffix: '1' as const,
        }));
      const metRenames = identityRenames
        .filter((r) => r.field.builder_type === 'metric')
        .map((r) => ({
          snap: r.field,
          prevColumnName: r.prevColumnName,
          roleSuffix: '0' as const,
        }));
      const migratedBase = migrateDescriptionIdentity(baseDesc, [
        ...dimRenames,
        ...metRenames,
      ]);
      if (migratedBase) {
        baseDesc = migratedBase;
        touched = true;
      }
      const migratedDraft = migrateDescriptionIdentity(draftDesc, [
        ...dimRenames,
        ...metRenames,
      ]);
      if (migratedDraft) {
        draftDesc = migratedDraft;
        touched = true;
      }
    }
  }

  // 无实际变更时不产出 patch，避免触发无意义的 draft 更新
  if (!touched) {
    return;
  }

  const basePrev = ctx.base.description;
  const draftPrev = ctx.draft.description;

  // 仅对发生变更的层生成 sub-key patch，合并为 base + draft 双写结果
  return patchBoth(
    baseDesc !== ctx.base.description
      ? descriptionLayerPatch(basePrev, baseDesc)
      : undefined,
    draftDesc !== ctx.draft.description
      ? descriptionLayerPatch(draftPrev, draftDesc)
      : undefined,
  );
}

/** QB 移除维/指时，裁剪 description 中对应 sub-key */
export const descriptionPruneHandler: IQBLinkageHandler = {
  id: 'description-prune',
  phase: 'prune',
  keys: ['description'],
  subscribe: ['metricRemoved', 'dimensionRemoved', 'rawColumnRemoved'],
  apply: (cs, ctx) => applyDescription(cs, ctx, 'prune'),
};

/** 字段 column_name 身份变更时，迁移 description sub-key */
export const descriptionMigrateHandler: IQBLinkageHandler = {
  id: 'description-migrate',
  phase: 'migrate',
  keys: ['description'],
  subscribe: ['fieldIdentityRenamed'],
  apply: (cs, ctx) => applyDescription(cs, ctx, 'migrate'),
};

/** @deprecated 使用 prune + migrate 两个 handler */
export const descriptionHandler = descriptionPruneHandler;
